import type { PageArgs } from "bunfx";
import { ClientError, navigateTo, RedirectError } from "bunfx";
import { Button } from "../components/ui";
import { usePageTitle } from "../hooks";
import { rpc } from "../rpc";

type LoaderData = { error: string; isExpired?: boolean; isSignup?: boolean };

export async function load({
  searchParams,
}: {
  searchParams: Record<string, string>;
}): Promise<LoaderData> {
  const userId = searchParams.user;
  const code = searchParams.code;
  const type = searchParams.type; // "signup" for email verification
  const redirectTo = decodeURIComponent(searchParams.redirectto || "/");

  if (!userId || !code) {
    return { error: "Invalid verification link" };
  }

  try {
    if (type === "signup") {
      // Email verification for signup - verify and redirect to profile completion
      await rpc.auth.verifySignup({ userId, code });
      throw new RedirectError("/signup/complete");
    }

    // Regular login code verification
    await rpc.auth.verifyLoginCode({ userId, code });
    throw new RedirectError(redirectTo);
  } catch (err) {
    // Let RedirectError propagate
    if (err instanceof RedirectError) {
      throw err;
    }

    const isExpired = err instanceof ClientError && err.code === "invalid_code";

    // If link expired but user is already logged in, just redirect
    if (isExpired) {
      try {
        const { user } = await rpc.auth.me({});
        if (!user) {
          throw new Error("Not logged in");
        }
        if (type !== "signup") {
          throw new RedirectError(redirectTo);
        }
        if (user.name) {
          throw new RedirectError("/");
        }
        throw new RedirectError("/signup/complete");
      } catch (innerErr) {
        // Let RedirectError propagate
        if (innerErr instanceof RedirectError) {
          throw innerErr;
        }
        // Not logged in, show error
      }
    }

    return {
      error: err instanceof ClientError ? err.message : "Something went wrong",
      isExpired,
      isSignup: type === "signup",
    };
  }
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Verify Email");
  const { error, isExpired, isSignup } = state.value;

  return (
    <div class="min-h-screen flex items-center justify-center px-4 py-12">
      <div class="auth-card text-center">
        <div class="text-4xl mb-4">❌</div>
        <h1 class="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">
          {isExpired ? "Link expired" : "Verification failed"}
        </h1>
        <p class="text-text-muted mb-6">
          {isExpired
            ? "Your verification link has expired. Please request a new one."
            : error}
        </p>
        <Button
          variant="secondary"
          class="w-full"
          onClick={() => navigateTo(isSignup ? "/signup" : "/forgotpassword")}
        >
          {isExpired ? "Request new link" : "Try again"}
        </Button>
      </div>
    </div>
  );
}
